import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AppBuilder from "./AppBuilder";
import IosAppBuilder from "./IosAppBuilder";
import AppPush from "./AppPush";
import StorePublication from "./StorePublication";
import {
  Smartphone, Lock, Check, Sparkles, Loader2, ShieldCheck,
  Palette, ImageIcon, Layers, Rocket, IndianRupee,
} from "lucide-react";

const FEATURES = [
  { icon: Layers, title: "Ready-made screens", text: "Home, categories, product details, cart, checkout, orders & account — all included." },
  { icon: Palette, title: "Your brand colours", text: "Pick a primary colour and the whole app is themed to match your store." },
  { icon: ImageIcon, title: "Your name & icon", text: "Set the app name and upload a launcher icon — no design skills needed." },
  { icon: ShieldCheck, title: "Connected to your store", text: "The app talks to your live store API — same products, prices and orders." },
  { icon: Rocket, title: "Play Store ready", text: "We build a real signed Android APK & AAB you can publish to Google Play." },
];

export default function MyApplication() {
  const [pkg, setPkg] = useState(null);
  const [tab, setTab] = useState("android");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();
  const [abpOn, setAbpOn] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/merchant/app-package");
      setPkg(data);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  useEffect(() => {
    const cfOrderId = params.get("cf_order_id");
    const run = async () => {
      if (cfOrderId) {
        setVerifying(true);
        try {
          const { data } = await api.get(`/merchant/app-package/verify/${cfOrderId}`);
          if (data.active) toast.success("Payment successful — App builder unlocked!");
          else toast.error("Payment not completed yet. If you paid, it will unlock shortly.");
        } catch (e) {
          toast.error(extractErr(e));
        } finally {
          // clean the query param
          params.delete("cf_order_id");
          setParams(params, { replace: true });
          setVerifying(false);
        }
      }
      await loadStatus();
      api.get("/merchant/config").then(({ data }) => setAbpOn(Boolean(data?.abp_capabilities_active))).catch(() => {});
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/app-package/checkout");
      const cashfree = await load({ mode: data.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
      // Browser redirects to Cashfree; on return we verify via cf_order_id.
    } catch (e) {
      toast.error(extractErr(e));
      setBusy(false);
    }
  };

  if (!pkg) {
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="myapp-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const active = pkg.status === "active";
  const price = pkg.price ?? 4000;
  const gstPercent = pkg.gst_percent ?? 18;
  const gstAmount = pkg.gst_amount ?? Math.round(price * 0.18 * 100) / 100;
  const totalPayable = pkg.total ?? Math.round((price + gstAmount) * 100) / 100;
  const inr2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
          <Smartphone className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">My Application</h1>
          <p className="text-slate-500 text-sm mt-1">Turn your store into a real mobile app your customers can install.</p>
        </div>
      </div>

      {verifying && (
        <Card className="p-4 flex items-center gap-2 text-slate-600" data-testid="myapp-verifying">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
        </Card>
      )}

      {active ? (
        <>
          <Card className="p-6 border-green-200 bg-green-50/60" data-testid="myapp-active">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center">
                <Check className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-slate-900">App builder unlocked</div>
                <div className="text-sm text-slate-600">{pkg.ios_active ? "Configure Android and iOS from the tabs below." : "Configure and build your Android app. iOS requires the iOS + Android + Website plan."}</div>
              </div>
            </div>
          </Card>

          <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit" data-testid="myapp-platform-tabs">
            <button type="button" data-testid="myapp-tab-android" onClick={() => setTab("android")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "android" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Android app</button>
            <button type="button" data-testid="myapp-tab-ios" onClick={() => setTab("ios")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "ios" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>iOS app</button>
            <button type="button" data-testid="myapp-tab-push" onClick={() => setTab("push")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "push" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Push Notification</button>
            <button type="button" data-testid="myapp-tab-publication" onClick={() => setTab("publication")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "publication" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Store Publication</button>
          </div>

          {abpOn && (tab === "android" || tab === "ios") ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="myapp-abp-notice">
              <b>ABP Capabilities</b> addon is active and will be added in app building. The Product Mixture section appears in the app after categories.
            </div>
          ) : null}

          {tab === "publication" ? <StorePublication /> : tab === "android" ? <AppBuilder /> : tab === "push" ? <AppPush /> : (
            pkg.ios_active ? <IosAppBuilder /> : (
              <Card className="p-6 border-slate-200" data-testid="ios-plan-locked">
                <div className="font-display font-bold text-lg">iOS builder is locked</div>
                <p className="text-sm text-slate-600 mt-2">The iOS builder unlocks only with the <b>iOS + Android + Website</b> yearly plan. Android stays available on Website + App. Ask sales or pick that plan at signup / domain checkout.</p>
              </Card>
            )
          )}
        </>
      ) : (
        <>
          <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit">
            <button type="button" data-testid="myapp-tab-publication" onClick={() => setTab("publication")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "publication" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Store Publication</button>
          </div>
          {tab === "publication" ? <StorePublication /> : null}
          {tab !== "publication" && <Card className="overflow-hidden" data-testid="myapp-locked">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold mb-4">
                <Lock className="w-3.5 h-3.5" /> Premium add-on
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold max-w-lg">
                Launch your own branded Android app
              </div>
              <p className="text-slate-300 text-sm mt-2 max-w-xl">
                We generate a real, Play-Store-ready Android app for your store — fully themed to your
                brand and connected to your live catalogue, cart and orders.
              </p>
              <div className="flex flex-wrap items-end gap-4 mt-6">
                <div className="flex items-center text-white">
                  <IndianRupee className="w-7 h-7" />
                  <span className="font-display text-4xl font-bold font-mono-nums">{price.toLocaleString("en-IN")}</span>
                  <span className="text-slate-300 text-sm ml-2 mb-1">one-time + GST</span>
                </div>
                <Button
                  onClick={startCheckout}
                  disabled={busy || !pkg.cashfree_configured}
                  data-testid="myapp-unlock-btn"
                  className="bg-blue-600 hover:bg-blue-700 h-11 px-6 text-base"
                >
                  {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>) : (<>Unlock for ₹{inr2(totalPayable)}</>)}
                </Button>
              </div>
              <div className="mt-3 text-xs text-slate-300" data-testid="myapp-gst">
                ₹{price.toLocaleString("en-IN")} + GST ({gstPercent}%) ₹{inr2(gstAmount)} = <b className="text-white">₹{inr2(totalPayable)}</b>
                <span className="text-slate-400"> · GSTIN 09FDFPB7488N1ZR · Grocode</span>
              </div>
              {!pkg.cashfree_configured && (
                <div className="text-xs text-amber-300 mt-3" data-testid="myapp-gateway-warn">
                  Payment gateway is not configured yet. Please contact support.
                </div>
              )}
            </div>
          </Card>}

          {tab !== "publication" && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <Card key={f.title} className="p-4 flex items-start gap-3">
                <f.icon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{f.title}</div>
                  <div className="text-xs text-slate-500">{f.text}</div>
                </div>
              </Card>
            ))}
          </div>}
        </>
      )}
    </div>
  );
}
